import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare, Send, Loader2, CheckCircle2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { submitContact } from '../services/api';

const CONTACT_EMAIL = 'guyanavibesinfo@gmail.com';
const CONTACT_SMS_NUMBER = '';  // set to +number when available; empty hides the iMessage button

const ContactPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || user?.display_name || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');
  const canSubmit = form.name.trim() && isValidEmail(form.email) && form.subject.trim() && form.message.trim() && form.message.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) {
      setError('Please fill in every field. Message must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim()
      });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const emailHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(form.subject || 'GuyanaVibes')}&body=${encodeURIComponent(form.message || '')}`;
  const smsHref = CONTACT_SMS_NUMBER ? `sms:${CONTACT_SMS_NUMBER}&body=${encodeURIComponent(form.message || '')}` : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#009E60]/5 to-white" data-testid="contact-page">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100" data-testid="contact-back-btn">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Contact us</h1>
            <p className="text-xs text-gray-500">We usually reply within 24 hours</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-2">
          <a
            href={emailHref}
            className="flex items-center justify-center gap-2 h-12 rounded-full bg-white border border-gray-200 hover:border-[#009E60] text-sm font-semibold text-gray-800"
            data-testid="email-us-btn"
          >
            <Mail className="w-4 h-4 text-[#009E60]" /> Email us
          </a>
          {smsHref ? (
            <a
              href={smsHref}
              className="flex items-center justify-center gap-2 h-12 rounded-full bg-white border border-gray-200 hover:border-[#009E60] text-sm font-semibold text-gray-800"
              data-testid="imessage-us-btn"
            >
              <MessageSquare className="w-4 h-4 text-blue-500" /> Message us
            </a>
          ) : (
            <div
              className="flex items-center justify-center gap-2 h-12 rounded-full bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-400 select-none"
              title="iMessage coming soon"
              data-testid="imessage-us-btn-disabled"
            >
              <MessageSquare className="w-4 h-4" /> Message us
            </div>
          )}
        </div>

        {/* Form */}
        {sent ? (
          <div className="mt-6 bg-white rounded-3xl shadow-sm p-6 text-center" data-testid="contact-sent">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#009E60]/10 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-8 h-8 text-[#009E60]" />
            </div>
            <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Message received</h2>
            <p className="text-sm text-gray-600 mt-1">Thanks for reaching out — we'll reply to <b>{form.email}</b> soon.</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button variant="outline" onClick={() => { setSent(false); setForm(prev => ({ ...prev, subject: '', message: '' })); }} className="rounded-full" data-testid="send-another-btn">
                Send another
              </Button>
              <Button onClick={() => navigate(user ? '/home' : '/')} className="rounded-full bg-[#009E60] hover:bg-[#007A4B]" data-testid="contact-done-btn">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-5 bg-white rounded-3xl shadow-sm p-5 space-y-4" data-testid="contact-form">
            <div>
              <Label className="text-sm font-semibold text-gray-800">Your name</Label>
              <Input value={form.name} onChange={(e) => update('name', e.target.value.slice(0, 120))} className="mt-1 h-11 rounded-xl" placeholder="How should we address you?" data-testid="contact-name" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-800">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value.slice(0, 200))} className="mt-1 h-11 rounded-xl" placeholder="you@example.com" data-testid="contact-email" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-800">Subject</Label>
              <Input value={form.subject} onChange={(e) => update('subject', e.target.value.slice(0, 200))} className="mt-1 h-11 rounded-xl" placeholder="What is this about?" data-testid="contact-subject" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-800">Message</Label>
              <Textarea value={form.message} onChange={(e) => update('message', e.target.value.slice(0, 5000))} className="mt-1 min-h-[140px] rounded-xl resize-none" placeholder="Tell us what's on your mind…" data-testid="contact-message" />
              <p className="text-xs text-gray-400 text-right mt-1">{form.message.length}/5000</p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 rounded-xl px-3 py-2 text-sm flex items-center gap-2" data-testid="contact-error">
                <X className="w-4 h-4" /> {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !canSubmit}
              className="w-full h-12 rounded-full bg-[#009E60] hover:bg-[#007A4B] text-white font-semibold"
              data-testid="contact-send-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {submitting ? 'Sending…' : 'Send message'}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Prefer email? <a href={emailHref} className="text-[#009E60] font-medium underline">Write to {CONTACT_EMAIL}</a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactPage;
